<template>
    <div>
        <v-expansion-panels
            v-if="parse != null"
            class="panels"
            v-model="panel_state"
        >
            <v-expansion-panel>
                <v-expansion-panel-header class="shaded">
                    <div class="d-flex align-center">
                        <span>
                            Style: {{ label }}
                        </span>
    
                        <v-tooltip bottom v-if="subheader">
                            <template #activator="{ on, attrs }">
                                <v-icon
                                    small
                                    class="ml-2"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-information-outline
                                </v-icon>
                            </template>
                            <span class="tooltip-capped">{{ subheader }}</span>
                        </v-tooltip>
                    </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="pt-3">
                    <div>
                        <m-edit-css-property
                            v-for="(item, index) in parse.properties"
                            :key="'_prop_' + index"
                            :label="item.name"
                            :value="item"
                            @modify="onModify"
                        />

                        <template v-if="parse_mobile && parse_mobile.properties?.length">
                            <div class="d-flex align-center mt-2 mb-4">
                                <v-icon
                                    small
                                    left
                                    color="primary"
                                >mdi-cellphone</v-icon>
                                <span class="subsection">Mobile-Only Styles</span>
                            </div>

                            <div class="mb-3">
                                <m-edit-css-property
                                    v-for="(item, index) in parse_mobile.properties"
                                    :key="'_mobile_prop_' + index"
                                    :label="item.name"
                                    :value="item"
                                    @modify="onModifyMobile"
                                />
                            </div>
                        </template>
                    </div>

                    <!-- Easy CSS Utilities -->
                    <div class="mb-3">
                        <div class="subsection mb-2">
                            <v-icon
                                small
                                left
                                color="primary"
                            >mdi-magic-staff</v-icon>
                            Quick Style Actions
                        </div>
                        <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                            <v-btn
                                small
                                outlined
                                color="primary"
                                @click="onStyleOp('add-thick-border')"
                            >
                                <v-icon
                                    small
                                    left
                                >mdi-border-all-variant</v-icon>
                                Thick Border
                            </v-btn>

                            <v-btn
                                small
                                outlined
                                color="primary"
                                @click="onStyleOp('add-thin-border')"
                            >
                                <v-icon
                                    small
                                    left
                                >mdi-border-all</v-icon>
                                Thin Border
                            </v-btn>

                            <v-btn
                                small
                                outlined
                                color="primary"
                                @click="onStyleOp('square-corners')"
                            >
                                <v-icon
                                    small
                                    left
                                >mdi-square-outline</v-icon>
                                Square Corners
                            </v-btn>

                            <v-btn
                                small
                                outlined
                                color="primary"
                                @click="onStyleOp('round-corners')"
                            >
                                <v-icon
                                    small
                                    left
                                >mdi-rounded-corner</v-icon>
                                Round Corners
                            </v-btn>

                            <v-btn
                                small
                                outlined
                                color="primary"
                                @click="onStyleOp('add-shadow')"
                            >
                                <v-icon
                                    small
                                    left
                                >mdi-image-filter-drama</v-icon>
                                Add Shadow
                            </v-btn>

                            <v-btn
                                small
                                outlined
                                color="primary"
                                @click="onStyleOp('add-padding')"
                            >
                                <v-icon
                                    small
                                    left
                                >mdi-format-indent-increase</v-icon>
                                Add Padding
                            </v-btn>
                        </div>
                    </div>

                    <div style="text-align: right">
                        <v-btn
                            fab
                            dark
                            color="primary"
                            style="margin-right: 15px"
                            @click="onClickReset"
                        >
                            <v-icon>
                                mdi-restore
                            </v-icon>
                        </v-btn>

                        <v-btn
                            fab
                            dark
                            color="primary"
                            @click="onClickAppend"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                        </v-btn>
                    </div>

                </v-expansion-panel-content>
            </v-expansion-panel>

        </v-expansion-panels>

    </div>
</template>

<script>
export default {
    props: {
        label: {
            type: String,
            default: function () { return 'Label'; }
        },
        subheader: {
            type: String,
            default: function () { return ''; }
        },
        value: {
            type: String,
            default: function () { return ''; }
        },
        default_value: {
            type: String,
            default: function () { return ''; }
        },
        mobile_value: {
            type: String,
            default: function () { return ''; }
        },
        default_mobile_value: {
            type: String,
            default: function () { return ''; }
        },
        originally_open: {
            type: Boolean,
            default: function () { return false; }
        },
    },

    data() {
        return {
            parse: null,
            parse_mobile: null,
            retry_count: 0,
            retry_count_mobile: 0,
            original_value: null,
            original_mobile_value: null,
            panel_state: this.originally_open ? 0 : null,
        }
    },

    methods: {
        queueParseValue() {
            // Add this component to the global CSS parsing queue
            // This avoids hammering the server when multiple components are created at the same time. If they all go together the server rejects some.
            if (!window.css_parse_queue) {
                window.css_parse_queue = [];
                window.css_parse_in_progress = false;
            }

            window.css_parse_queue.push(this);

            if (!window.css_parse_in_progress) {
                this.process_queue();
            }
        },

        queueParseValueMobile() {
            if (!window.css_parse_queue_mobile) {
                window.css_parse_queue_mobile = [];
                window.css_parse_in_progress_mobile = false;
            }
            window.css_parse_queue_mobile.push(this);

            if (!window.css_parse_in_progress_mobile) {
                this.process_queue({ is_mobile: true });
            }
        },

        process_queue({ is_mobile = false } = {}) {
            const queue = is_mobile ? window.css_parse_queue_mobile : window.css_parse_queue;
            const in_progress_key = is_mobile ? 'css_parse_in_progress_mobile' : 'css_parse_in_progress';

            if (queue.length === 0) {
                window[in_progress_key] = false;
                return;
            }

            window[in_progress_key] = true;
            const component = queue.shift();

            const method = is_mobile ? 'parseMobileValue' : 'parseValue';
            component[method]();
        },

        parseValue() {
            const timeout_id = setTimeout(() => {
                this.handleParseComplete();
                if (this.retry_count < 3) {
                    this.retry_count++;
                    setTimeout(() => {
                        this.queueParseValue();
                    }, 1000);
                } else {
                    this.parse = {
                        properties: []
                    };
                }
            }, 8000);

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/css/inline/parse',

                'params': {
                    'inline_style': this.value,
                    'default_inline_style': this.default_value,
                },

                'on_success': (data) => {
                    clearTimeout(timeout_id);
                    this.parse = data;
                    this.retry_count = 0;
                    this.handleParseComplete();
                },

                'on_error': () => {
                    clearTimeout(timeout_id);

                    if (this.retry_count < 3) {
                        this.retry_count++;
                        const delay = this.retry_count * 500; // 500ms, 1s, 1.5s delays
                        setTimeout(() => {
                            this.parseValue();
                        }, delay);
                    } else {
                        this.parse = {
                            properties: []
                        };
                        this.handleParseComplete();
                    }
                },
            });
        },

        parseMobileValue() {
            const timeout_id = setTimeout(() => {
                this.handleMobileParseComplete();
                if (this.retry_count_mobile < 3) {
                    this.retry_count_mobile++;
                    setTimeout(() => {
                        this.queueParseValueMobile();
                    }, 1000);
                } else {
                    this.parse = {
                        properties: []
                    };
                }
            }, 8000);

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/css/inline/parse',

                'params': {
                    'inline_style': this.mobile_value,
                    'default_inline_style': this.default_mobile_value,
                },

                'on_success': (data) => {
                    clearTimeout(timeout_id);
                    this.parse_mobile = data;
                    this.retry_count_mobile = 0;
                    this.handleMobileParseComplete();
                },

                'on_error': () => {
                    clearTimeout(timeout_id);

                    if (this.retry_count_mobile < 3) {
                        this.retry_count_mobile++;
                        const delay = this.retry_count_mobile * 500; // 500ms, 1s, 1.5s delays
                        setTimeout(() => {
                            this.parseMobileValue();
                        }, delay);
                    } else {
                        this.parse_mobile = {
                            properties: []
                        };
                        this.handleMobileParseComplete();
                    }
                },
            });
        },

        handleParseComplete() {
            setTimeout(() => {
                this.process_queue();
            }, 100);
        },

        handleMobileParseComplete() {
            setTimeout(() => {
                this.process_queue({ is_mobile: true });
            }, 100);
        },

        onModify(operation) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/css/inline/modify',

                'params': {
                    'inline_style': this.value,
                    'default_inline_style': this.default_value,
                    ...operation,
                },

                'on_success': (data) => {
                    this.parse = data;
                    this.$emit('input', data.inline_style);
                },

                'on_error': 'Unable to modify styling',
            });
        },

        onModifyMobile(operation) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/css/inline/modify',

                'params': {
                    'inline_style': this.mobile_value,
                    'default_inline_style': this.default_mobile_value,
                    ...operation,
                },

                'on_success': (data) => {
                    this.parse_mobile = data;
                    this.$emit('update:mobile_value', data.inline_style);
                    this.$emit('mobileValueUpdated', data.inline_style);
                },

                'on_error': 'Unable to modify mobile styling',
            });
        },

        onClickReset() {
            this.$rc.confirm({
                'title': 'Reset Styling: ' + this.label,
                'body': 'Do you want to reset the styling to the values from when you first loaded the page?<br/><br/>This can not be undone.',
                'btn_text_ok': 'Reset',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doReset();
                },
            });
        },

        doReset() {
            this.$emit('update:mobile_value', this.original_mobile_value ?? this.default_mobile_value);
            this.$emit('input', this.original_value ?? this.default_value);
        },

        onClickAppend() {
            this.$rc.prompt({
                'title': 'Add CSS',
                'label': 'CSS to append',
                'value': '',
                'unset_label': null,

                'after_prompt': (value) => {
                    this.onModify({
                        'op': 'append',
                        'value': value
                    });
                },
            });
        },

        onStyleOp(operation) {
            this.onModify({
                'op': operation
            });
        }
    },

    watch: {
        value() {
            this.retry_count = 0;
            this.queueParseValue();
        },
        mobile_value() {
            this.retry_count_mobile = 0;
            this.queueParseValueMobile();
        }
    },

    mounted() {
        this.queueParseValue();
        this.queueParseValueMobile();
        this.original_value = this.value;
        this.original_mobile_value = this.mobile_value;
    },
}
</script>

<style lang="scss" scoped>
.panels {
    margin-bottom: 15px;
    border-left: 1px solid rgba(0, 0, 0, 0.1);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.subsection {
    font-size: 1rem;
}
</style>